import "./WaybillNoColumn.css";
import React from "react";
import WaybillNoInput from "./WaybillNoInput";
import classNames from "classnames";
import { OrderType } from "@/scenes/OrderManagement/redux/states/TableState";
import ShopType from "@/enum/ShopType";
import LogisticsCompanyNameMap from "@/map/LogisticsCompanyNameMap";
import OrderDTO from "@/dto/order/OrderDTO";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { observer } from "mobx-react-lite";
import { hasEwaybillNo } from "@/utils/OrderUtils";
import EwaybillNoComponents from "./WaybillNo/components/EwaybillNoComponents";
import OrderTag from "@/enum/OrderTag";
import { useGlobalStore } from "@/store/hooks";
import WaybillTemplateType from "@/enum/WaybillTemplateType";
import LogisticsCompany from "@/enum/LogisticsCompany";
import { Link } from "react-router-dom";

interface WaybillNoColumnProps {
    order: OrderType;

    rowIndex: number;

    shopType: ShopType;
}

/** 是否为导入的运单号(导入运单号发货中不勾选导入后发货) */
export function isImportedWaybillNo(order: OrderDTO) {
    if (!order.waybillNo) return false;

    /** 单号在代发包裹中 */
    if (order.retailerDropShippingInfo?.packages?.length) {
        for (const dropShippingPackage of order.retailerDropShippingInfo.packages) {
            if (dropShippingPackage.waybillNo === order.waybillNo) {
                return false;
            }
        }
    }

    /** 单号在电子面单中 */
    for (const ewaybill of order.ewaybills || []) {
        const flag = ewaybill.waybillNo === order.waybillNo;

        if (ewaybill.parentWaybillNo) {
            if (ewaybill.parentWaybillNo === order.waybillNo || flag) {
                return false;
            }
        } else {
            if (flag) {
                return false;
            }
        }
    }

    return true;
}

const TaoBaoSFTip = observer((props: { order: OrderDTO }) => {
    const { order } = props;

    const globalStore = useGlobalStore();

    if ([ShopType.TAOBAO].includes(globalStore.shopMapping.get(order.shopId)?.type)) {
        if (order.tags?.includes(OrderTag.TAOTIAN_SHIP_BY_SF_JC)) {
            if (globalStore.waybillPrintStore.waybillTemplatesLoading) return null;

            const hasCnSf = globalStore.waybillPrintStore.waybillTemplates?.some(
                (template) =>
                    [WaybillTemplateType.CAINIAO, WaybillTemplateType.TAOBAO_CAINIAO].includes(template.type) &&
                    template.logisticsCompany === LogisticsCompany.SF,
            );

            if (!hasCnSf) {
                return (
                    <p>
                        这是加价顺丰托管运单，您还没有顺丰模版，
                        <Link to={`/setting/waybillTemplate/repository?logisticsCompany=${LogisticsCompany.SF}`}>
                            点击快速配置一下吧
                        </Link>
                    </p>
                );
            }
        }
    }

    return null;
});

/**
 * ❗需要同步修改SearchWaybillNosColumn.tsx的内容❗
 */
const WaybillNoColumn: React.FC<WaybillNoColumnProps> = ({ order, shopType }) => {
    const orderManagementStore = useOrderManagementStore();

    function handleUpdateMainEwaybillSuccess(order: OrderDTO) {
        orderManagementStore.table.updateOrder(order.id, order);
    }

    const hasCancelEwaybill = order.tags?.includes(OrderTag.CANCEL_WAYBILL_RECYCLED);

    let component: React.ReactNode = null;

    if (hasEwaybillNo(order)) {
        if (isImportedWaybillNo(order)) {
            component = (
                <>
                    <WaybillNoInput order={order} shopType={shopType} />

                    <EwaybillNoComponents
                        order={order}
                        handleUpdateMainEwaybillSuccess={handleUpdateMainEwaybillSuccess}
                        shopType={shopType}
                    />
                </>
            );
        } else {
            component = (
                <EwaybillNoComponents
                    order={order}
                    handleUpdateMainEwaybillSuccess={handleUpdateMainEwaybillSuccess}
                    shopType={shopType}
                />
            );
        }
    } else {
        component = <WaybillNoInput order={order} shopType={shopType} />;
    }

    return (
        <td
            className={classNames("waybill-no-col", {
                error: !!order.waybillNoInputValidateErrorMsg,
            })}
        >
            {order?.designatedLogisticsCompany && (
                <p className="small font-bold text-danger">
                    指定物流：{LogisticsCompanyNameMap[order.designatedLogisticsCompany]}
                </p>
            )}
            {hasCancelEwaybill ? <p className="text-gray small mb5">订单有回收运单号</p> : null}
            <TaoBaoSFTip order={order} />
            {component}
        </td>
    );
};

export default observer(WaybillNoColumn);
